/**
 * 手机号验证工具函数
 * 用于登录、注册、更换手机号等场景的手机号验证
 */

/** 手机号类型 */
export type MobileType = 0 | 1; // 0: 国内, 1: 国际

/** 验证码类型 */
export type MessageType = 0 | 1; // 0: 短信, 1: 语音

/** 发送验证码的场景类型 */
export enum SendCodeType {
  /** 注册 */
  REGISTER = 1,
  /** 忘记密码 */
  FORGET_PASSWORD = 2,
  /** 登录 */
  LOGIN = 3,
  /** 修改手机号 */
  RESET_MOBILE = 4,
  /** 绑定提现账号 */
  BIND_WITHDRAWAL = 5,
  /** 注销用户 */
  DELETE_ACCOUNT = 6,
  /** 修改邮箱 */
  RESET_EMAIL = 7,
  /** 绑定邮箱 */
  BIND_EMAIL = 8,
}

/** 手机号验证结果 */
export interface ValidateResult {
  valid: boolean
  message: string
}

/** 手机号类型选项 */
export const mobileTypeOptions = [
  { label: '国内', value: 0 as MobileType },
  { label: '国际', value: 1 as MobileType },
];

/** 验证码类型选项 */
export const messageTypeOptions = [
  { label: '短信', value: 0 as MessageType },
  { label: '语音', value: 1 as MessageType },
];

/** 手机号最大输入长度 */
export const MOBILE_MAX_LENGTH = {
  /** 国内手机号固定11位 */
  DOMESTIC: 11,
  /** 国际手机号含国家码，E.164 最长15位，兼容 00 前缀放宽至20位 */
  INTERNATIONAL: 20,
};

/**
 * 获取手机号输入框的最大长度
 * @param mobileType 手机号类型 0:国内 1:国际
 * @returns 最大输入长度
 */
export function getMobileMaxLength(mobileType: MobileType = 0): number {
  return mobileType === 0 ? MOBILE_MAX_LENGTH.DOMESTIC : MOBILE_MAX_LENGTH.INTERNATIONAL;
}

/**
 * 验证国内手机号格式
 * @param mobile 手机号
 * @returns 是否有效
 */
export function isValidChinaMobile(mobile: string): boolean {
  const reg = /^1[3-9]\d{9}$/;
  return reg.test(mobile);
}

/**
 * 验证国际手机号格式（简单验证长度区间）
 * @param mobile 手机号
 * @returns 是否有效
 */
export function isValidInternationalMobile(mobile: string): boolean {
  return mobile.length >= 5 && mobile.length <= MOBILE_MAX_LENGTH.INTERNATIONAL;
}

/**
 * 验证手机号
 * @param mobile 手机号
 * @param mobileType 手机号类型 0:国内 1:国际
 * @returns 验证结果
 */
export function validateMobile(mobile: string, mobileType: MobileType = 0): ValidateResult {
  if (!mobile) {
    return { valid: false, message: '请输入手机号' };
  }

  if (mobileType === 0) {
    // 国内手机号验证
    if (!isValidChinaMobile(mobile)) {
      return { valid: false, message: '请输入正确的手机号' };
    }
  }
  else {
    // 国际手机号验证
    if (!isValidInternationalMobile(mobile)) {
      return { valid: false, message: '请输入正确的手机号' };
    }
  }

  return { valid: true, message: '' };
}

/**
 * 验证密码格式
 * @param password 密码
 * @param minLength 最小长度，默认6
 * @param maxLength 最大长度，默认20
 * @returns 验证结果
 */
export function validatePassword(
  password: string,
  minLength: number = 6,
  maxLength: number = 20,
): ValidateResult {
  if (!password) {
    return { valid: false, message: '请输入密码' };
  }

  if (password.length < minLength || password.length > maxLength) {
    return { valid: false, message: `密码长度应为${minLength}-${maxLength}位` };
  }

  return { valid: true, message: '' };
}

/**
 * 验证验证码格式
 * @param code 验证码
 * @param length 验证码长度，默认4
 * @returns 验证结果
 */
export function validateCode(code: string, length: number = 4): ValidateResult {
  if (!code) {
    return { valid: false, message: '请输入验证码' };
  }

  if (code.length < length) {
    return { valid: false, message: `验证码应为${length}位` };
  }

  return { valid: true, message: '' };
}

/**
 * 验证用户协议是否同意
 * @param agreed 是否同意
 * @returns 验证结果
 */
export function validateProtocol(agreed: boolean): ValidateResult {
  if (!agreed) {
    return { valid: false, message: '请先同意并勾选用户协议和代发协议' };
  }
  return { valid: true, message: '' };
}

/**
 * 格式化手机号（隐藏中间部分）
 * @param mobile 手机号
 * @returns 格式化后的手机号，如：138****8888
 */
export function formatMobile(mobile: string): string {
  if (!mobile || mobile.length < 7) return mobile;
  return mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
}

/**
 * 获取手机号类型文本
 * @param type 手机号类型
 * @returns 类型文本
 */
export function getMobileTypeText(type: MobileType): string {
  return mobileTypeOptions.find(item => item.value === type)?.label || '国内';
}

/**
 * 获取验证码类型文本
 * @param type 验证码类型
 * @returns 类型文本
 */
export function getMessageTypeText(type: MessageType): string {
  return messageTypeOptions.find(item => item.value === type)?.label || '短信';
}
